import React from 'react'
import { cn } from '@/lib/utils'
import type { Feedback } from '../../../slides/renderer-internal-types'
import { CHART_LIMITS, INTERACTION_LIMITS } from '../../../foundations/slide-limits'
import { ChartTooltip, type ChartTooltipData } from '../../feedback/ChartTooltip/ChartTooltip'
import { ChartLabel } from '../../../plots/_shared/ChartLabel'
import { ChartLabelFullText } from '../../../plots/_shared/ChartLabelFullText'

export interface QuadrantChartItem {
  name: string
  /** x value on 1-5 scale (Trust) */
  x: number
  /** y value on 1-5 scale (Attention) */
  y: number
  /** Optional bubble size (0-1 or 1-5 scale) */
  size?: number
  feedback?: Feedback | null
}

export interface QuadrantChartProps {
  items: QuadrantChartItem[]
  xAxisLabel: string
  yAxisLabel: string
  /** Plots plan LB: an axis title's source text when the chart prints another title. */
  xAxisFullLabel?: string | null
  yAxisFullLabel?: string | null
  /** A static render (the PDF) keeps the ellipsis, measures nothing and draws no (i). */
  renderMode?: 'interactive' | 'static'
  quadrantLabels?: {
    topLeft?: string
    topRight?: string
    bottomLeft?: string
    bottomRight?: string
  }
  /** Optional background watermark labels. Never copied from quadrantLabels (the corner labels already name the quadrants); placeholders only when neither is given. */
  backgroundQuadrantLabels?: {
    topLeft?: string
    topRight?: string
    bottomLeft?: string
    bottomRight?: string
  }
  /** Controls quadrant background tinting. */
  backgroundStyle?: 'semantic' | 'neutral'
  /** Controls dot color assignment. */
  dotColorMode?: 'semantic' | 'quadrant'
  /** Optional dot colors by quadrant when dotColorMode='quadrant'. */
  dotColors?: {
    topLeft?: string
    topRight?: string
    bottomLeft?: string
    bottomRight?: string
  }
  /** Optional dot colors by item name (overrides dotColorMode). */
  dotColorsByName?: Record<string, string>
  /** Optional bubble sizing overrides (defaults to range derived from items). */
  sizeMin?: number
  sizeMax?: number
  /** Optional small icon URLs to show near each quadrant label (e.g., pain icons) */
  quadrantIconUrls?: {
    topLeft?: string[]
    topRight?: string[]
    bottomLeft?: string[]
    bottomRight?: string[]
  }
  /** Back-compat: if provided, used for both x/y scales unless x/y overrides exist */
  scaleMin?: number
  /** Back-compat: if provided, used for both x/y scales unless x/y overrides exist */
  scaleMax?: number
  /** Optional x-axis scale overrides */
  xScaleMin?: number
  xScaleMax?: number
  /** Optional y-axis scale overrides */
  yScaleMin?: number
  yScaleMax?: number
  /**
   * Where the crosshair splits each axis, in the axis's own units. Defaults to
   * the scale's middle. A producer that stamps its midpoint (the `quadrant`
   * plot) passes it here, so the renderer never decides where the split is.
   */
  xMidpoint?: number
  yMidpoint?: number
  /** Optional tooltip data keyed by item.name */
  tooltipDataByName?: Record<string, ChartTooltipData>
  /** Hide inline text labels above dots; use the legend below instead. Defaults to true. */
  showInlineLabels?: boolean
  /** Show a color-coded legend below the chart. Defaults to false. */
  showLegend?: boolean
  /**
   * Inline labels only: a label's place in a stack of names that share one spot,
   * keyed by item name (0, or absent, is the usual place above the dot). The
   * caller decides which points coincide; the chart only moves the names.
   */
  labelStackByName?: Record<string, number>
  className?: string
  /** Renderer-local native geometry; never part of a stored plot. */
  onMinimumSize?: (size: { width: number; height: number; feasible: boolean }) => void
}

function colorForPoint(x: number, y: number, midX: number, midY: number) {
  // Reference semantics (x=Trust, y=Attention):
  // - High attn + low trust => Clickbait Risk (red)
  // - High attn + high trust => Scale Zone (green)
  // - Low attn + high trust => Quiet Winner (yellow)
  // - Low attn + low trust => Underwhelming (grey)
  const highX = x >= midX
  const highY = y >= midY
  if (!highX && highY) return { dot: 'var(--slide-danger)', quadrant: 'Clickbait Risk' }
  if (highX && highY) return { dot: 'var(--slide-success)', quadrant: 'Scale Zone' }
  if (highX && !highY) return { dot: 'var(--slide-warning)', quadrant: 'Quiet Winner' }
  return { dot: 'var(--slide-text-muted)', quadrant: 'Underwhelming' }
}

interface QuadrantYAxisTitleProps {
  label: string
  fullLabel?: string | null
  renderMode: 'interactive' | 'static'
  geometryRef?: (node: HTMLDivElement | null) => void
}

/**
 * The y-axis title reads bottom to top in the left gutter. The shared clamp
 * measures horizontal lines, so this one line of vertical text carries its own
 * ellipsis and overflow check, and hands the result to the shared (i).
 */
function QuadrantYAxisTitle(props: QuadrantYAxisTitleProps) {
  const { label, fullLabel, renderMode } = props

  const textRef = React.useRef<HTMLDivElement | null>(null)
  const [isTruncated, setIsTruncated] = React.useState(false)

  const isStatic = renderMode === 'static'

  React.useLayoutEffect(() => {
    const node = textRef.current
    if (isStatic || !node) {
      setIsTruncated(false)
      return
    }
    const measure = () => {
      setIsTruncated(node.scrollHeight > node.clientHeight + 1)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [label, isStatic])

  return (
    <>
      <div
        ref={(node) => { textRef.current = node; props.geometryRef?.(node) }}
        data-testid="quadrant-chart-y-axis-title"
        style={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          lineHeight: 'var(--line-height-none)',
          whiteSpace: 'nowrap',
          overflow: props.geometryRef ? 'visible' : 'hidden',
          textOverflow: props.geometryRef ? undefined : 'ellipsis',
          minHeight: 0,
          maxHeight: props.geometryRef ? undefined : '100%',
          flexShrink: props.geometryRef ? 0 : undefined,
        }}
      >
        {label}
      </div>
      <ChartLabelFullText label={label} fullText={fullLabel} truncated={isTruncated} index={1} size={11} renderMode={renderMode} />
    </>
  )
}

export function QuadrantChart({
  items,
  xAxisLabel,
  yAxisLabel,
  xAxisFullLabel,
  yAxisFullLabel,
  renderMode = 'interactive',
  quadrantLabels,
  backgroundQuadrantLabels,
  backgroundStyle = 'neutral',
  dotColorMode = 'semantic',
  dotColors,
  dotColorsByName,
  sizeMin,
  sizeMax,
  quadrantIconUrls,
  scaleMin = 1,
  scaleMax = 5,
  xScaleMin,
  xScaleMax,
  yScaleMin,
  yScaleMax,
  xMidpoint,
  yMidpoint,
  tooltipDataByName,
  showInlineLabels = true,
  showLegend = false,
  labelStackByName,
  className,
  onMinimumSize,
}: QuadrantChartProps) {
  const xMin = xScaleMin ?? scaleMin
  const xMax = xScaleMax ?? scaleMax
  const yMin = yScaleMin ?? scaleMin
  const yMax = yScaleMax ?? scaleMax
  const midX = typeof xMidpoint === 'number' && Number.isFinite(xMidpoint) ? xMidpoint : (xMin + xMax) / 2
  const midY = typeof yMidpoint === 'number' && Number.isFinite(yMidpoint) ? yMidpoint : (yMin + yMax) / 2

  // Limit items to prevent DOM explosion with too many dots
  const limitedItems = React.useMemo(() => {
    return items.slice(0, CHART_LIMITS.quadrantMaxScatterPoints)
  }, [items])

  const derivedSizeRange = React.useMemo(() => {
    const vals = limitedItems
      .map((it) => (Number.isFinite(it.size as number) ? Number(it.size) : undefined))
      .filter((v): v is number => typeof v === 'number')
    if (!vals.length) return { min: 0, max: 1 }
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    return { min, max: max === min ? min + 1 : max }
  }, [limitedItems])

  const sMin = typeof sizeMin === 'number' ? sizeMin : derivedSizeRange.min
  const sMax = typeof sizeMax === 'number' ? sizeMax : derivedSizeRange.max

  // One source per quadrant name. `quadrantLabels` are drawn as the corner labels
  // below; repeating them as the centred watermark printed every name twice. The
  // watermark shows only labels passed for it, and keeps its placeholders only
  // for a caller that names the quadrants nowhere.
  const effectiveBgLabels: NonNullable<QuadrantChartProps['backgroundQuadrantLabels']> =
    backgroundQuadrantLabels ||
    (quadrantLabels
      ? {}
      : {
          topLeft: 'Top-left',
          topRight: 'Top-right',
          bottomLeft: 'Bottom-left',
          bottomRight: 'Bottom-right',
        })

  const quadrantBg = (() => {
    if (backgroundStyle === 'semantic') {
      return {
        topLeft: 'var(--slide-danger-soft)',
        topRight: 'var(--slide-success-soft)',
        bottomLeft: 'var(--slide-neutral-light)',
        bottomRight: 'var(--slide-warning-soft)',
      }
    }
    // Neutral (token-driven) default
    return {
      topLeft: 'color-mix(in srgb, var(--slide-warning) 8%, var(--slide-card-bg))',
      topRight: 'color-mix(in srgb, var(--slide-accent-alt) 10%, var(--slide-card-bg))',
      bottomLeft: 'var(--slide-card-bg)',
      bottomRight: 'color-mix(in srgb, var(--slide-success) 8%, var(--slide-card-bg))',
    }
  })()

  const effectiveDotColors = dotColors || {
    topLeft: 'var(--slide-warning)',
    topRight: 'var(--slide-accent-alt)',
    bottomLeft: 'var(--slide-text-muted)',
    bottomRight: 'var(--slide-text-secondary)',
  }

  const toPct = (v: number, min: number, max: number) => {
    // Guard against non-finite values and division by zero
    if (!Number.isFinite(v) || !Number.isFinite(min) || !Number.isFinite(max)) {
      return 50 // Center fallback
    }
    const range = max - min
    if (range === 0) return 50 // Center fallback for collapsed range
    const clamped = Math.max(min, Math.min(max, v))
    return ((clamped - min) / range) * 100
  }

  const pointPosition = (item: QuadrantChartItem) => ({
    left: Math.max(2, Math.min(98, toPct(item.x, xMin, xMax))),
    bottom: Math.max(2, Math.min(98, toPct(item.y, yMin, yMax))),
  })

  // The crosshair's position inside the plot area, in percent of its width
  // (from the left) and height (from the top). At the scale's middle this is
  // 50/50, exactly the halves the boxes always were.
  const splitLeftPct = toPct(midX, xMin, xMax)
  const splitTopPct = 100 - toPct(midY, yMin, yMax)
  const boxSize = {
    topLeft: { width: `${splitLeftPct}%`, height: `${splitTopPct}%` },
    topRight: { width: `${100 - splitLeftPct}%`, height: `${splitTopPct}%` },
    bottomLeft: { width: `${splitLeftPct}%`, height: `${100 - splitTopPct}%` },
    bottomRight: { width: `${100 - splitLeftPct}%`, height: `${100 - splitTopPct}%` },
  }

  const [nativeLabelStep, setNativeLabelStep] = React.useState<number | null>(null)
  const chartRef = React.useRef<HTMLDivElement>(null)
  const planeRef = React.useRef<HTMLDivElement>(null)
  const labels = React.useRef(new Map<string, HTMLElement>())
  const registerLabel = (key: string) => (node: HTMLElement | null) => {
    if (node) labels.current.set(key, node)
    else labels.current.delete(key)
  }
  React.useLayoutEffect(() => {
    if (!onMinimumSize) return
    let active = true
    const measure = () => {
      const chart = chartRef.current
      const plane = planeRef.current
      if (!active || !chart || !plane) return
      let width = labels.current.get('x-axis')?.scrollWidth ?? 0
      let height = labels.current.get('y-axis')?.scrollHeight ?? 0
      let feasible = true
      const labelGap = parseFloat(getComputedStyle(chart).getPropertyValue('--ds-gap-xs'))
      const labelHeight = Math.max(0, ...limitedItems.map((_, index) => labels.current.get(`point-${index}`)?.scrollHeight ?? 0))
      const nextStep = labelHeight + labelGap
      if (!Object.is(nativeLabelStep, nextStep)) {
        setNativeLabelStep(nextStep)
        return // Measure extents only after the declared native stride is applied.
      }
      for (const [key, top, left] of [['corner-topLeft', true, true], ['corner-topRight', true, false], ['corner-bottomLeft', false, true], ['corner-bottomRight', false, false]] as const) {
        const label = labels.current.get(key)
        if (!label) continue
        const style = getComputedStyle(label)
        const verticalPadding = parseFloat(top ? style.top : style.bottom)
        const horizontalPadding = parseFloat(left ? style.left : style.right)
        const hFraction = (top ? splitTopPct : 100 - splitTopPct) / 100
        const wFraction = (left ? splitLeftPct : 100 - splitLeftPct) / 100
        const cornerHeight = label.scrollHeight + verticalPadding
        const cornerWidth = label.scrollWidth + horizontalPadding
        height = Math.max(height, cornerHeight)
        width = Math.max(width, cornerWidth)
        if (hFraction <= 0 || wFraction <= 0) feasible = false
        else {
          height = Math.max(height, cornerHeight / hFraction)
          width = Math.max(width, cornerWidth / wFraction)
        }
      }
      const pointLabels: Array<{ left: number; right: number; top: number; bottom: number }> = []
      for (let index = 0; index < limitedItems.length; index++) {
        const item = limitedItems[index]
        const label = labels.current.get(`point-${index}`)
        if (!label) continue
        const position = pointPosition(item)
        const x = position.left / 100
        const bottom = position.bottom / 100
        const top = 1 - bottom
        const dot = label.parentElement!
        const labelTop = parseFloat(getComputedStyle(label).top)
        // Fixed native fonts and dots stay full size. Required coordinate-plane
        // dimensions follow the actual drawn position, not a guessed pixel floor.
        const upward = Math.max(dot.clientHeight / 2, dot.clientHeight / 2 - labelTop)
        const downward = Math.max(dot.clientHeight / 2, labelTop + label.scrollHeight - dot.clientHeight / 2)
        const leftExtent = Math.max(dot.clientWidth / 2, label.clientWidth / 2)
        const rightExtent = Math.max(dot.clientWidth / 2, label.scrollWidth - label.clientWidth / 2)
        height = Math.max(height, upward / top, downward / bottom)
        width = Math.max(width, leftExtent / x, rightExtent / (1 - x))
        const labelLeft = x * plane.clientWidth - label.clientWidth / 2
        const labelY = top * plane.clientHeight - dot.clientHeight / 2 + labelTop
        pointLabels.push({ left: labelLeft, right: labelLeft + label.scrollWidth, top: labelY, bottom: labelY + label.scrollHeight })
      }
      for (let i = 0; i < pointLabels.length; i++) for (let j = i + 1; j < pointLabels.length; j++) {
        const a = pointLabels[i], b = pointLabels[j]
        if (a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom) feasible = false
      }
      onMinimumSize({ width: width + chart.clientWidth - plane.clientWidth, height: height + chart.clientHeight - plane.clientHeight, feasible })
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ;[chartRef.current, planeRef.current, ...labels.current.values()].forEach((node) => node && observer?.observe(node))
    void document.fonts?.ready.then(measure)
    return () => { active = false; observer?.disconnect() }
  }, [onMinimumSize, limitedItems, splitTopPct, splitLeftPct, xMin, xMax, yMin, yMax, quadrantLabels, labelStackByName, nativeLabelStep])

  return (
    <div ref={chartRef} className={cn('relative w-full h-full', className)}>
      {/* Plot area (everything that uses x/y coordinates).
          `--quadrant-axis-offset-x/y` (the axis-label gutters) are consumed with inline
          fallbacks: no surface in this repo defines them, and an undefined var made the
          absolutely-positioned plot area's left/bottom invalid — the whole plot collapsed
          to a shrink-to-fit box at the top-right (chat AND report). A token layer that
          defines the vars still wins over the fallbacks.

          The same held for the other six `--quadrant-*` vars used below (label offset,
          label pad, label max-width, icon size, tooltip width, tooltip offset): nothing
          in this repo defines any of them either. An undefined var dropped the property,
          so the four corner labels fell back to their static position and stacked in one
          corner, the quadrant icons drew at their intrinsic pixel size and the feedback
          popup shrank to fit. Each now carries the same kind of inline fallback. */}
      <div
        ref={planeRef}
        className="absolute"
        style={{
          top: 0,
          right: 0,
          left: 'var(--quadrant-axis-offset-x, 2.25rem)',
          bottom: 'var(--quadrant-axis-offset-y, 2rem)',
        }}
      >
        <div className="relative w-full h-full">
          {/* Quadrants */}
          <div className="absolute inset-0">
        <div
          className="absolute top-0 left-0 border-r border-dashed border-b border-[color:var(--slide-border)] flex items-center justify-center"
          style={{ ...boxSize.topLeft, background: quadrantBg.topLeft }}
        >
          <div
            className="pointer-events-none"
            style={{
              fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'color-mix(in srgb, var(--slide-danger) 12%, transparent)',
            }}
          >
            {effectiveBgLabels.topLeft || ''}
          </div>
        </div>
        <div
          className="absolute top-0 right-0 border-b border-dashed border-[color:var(--slide-border)] flex items-center justify-center"
          style={{ ...boxSize.topRight, background: quadrantBg.topRight }}
        >
          <div
            className="pointer-events-none"
            style={{
              fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'color-mix(in srgb, var(--slide-success) 12%, transparent)',
            }}
          >
            {effectiveBgLabels.topRight || ''}
          </div>
        </div>
        <div
          className="absolute bottom-0 left-0 border-r border-dashed border-[color:var(--slide-border)] flex items-center justify-center"
          style={{ ...boxSize.bottomLeft, background: quadrantBg.bottomLeft }}
        >
          <div
            className="pointer-events-none"
            style={{
              fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'color-mix(in srgb, var(--slide-text-primary) 6%, transparent)',
            }}
          >
            {effectiveBgLabels.bottomLeft || ''}
          </div>
        </div>
        <div
          className="absolute bottom-0 right-0 flex items-center justify-center"
          style={{ ...boxSize.bottomRight, background: quadrantBg.bottomRight }}
        >
          <div
            className="pointer-events-none"
            style={{
              fontSize: 'var(--font-size-h3)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'color-mix(in srgb, var(--slide-warning) 12%, transparent)',
            }}
          >
            {effectiveBgLabels.bottomRight || ''}
          </div>
        </div>
      </div>

          {/* Quadrant labels */}
          {quadrantLabels ? (
            <>
              {quadrantLabels.topLeft ? (
                <div
                  ref={registerLabel('corner-topLeft')}
                  className="absolute text-[color:var(--slide-text-muted)]"
                  style={{
                    top: 'var(--quadrant-label-offset, 0.5rem)',
                    left: 'var(--quadrant-label-pad, 0.75rem)',
                    maxWidth: 'var(--quadrant-label-max-width, 42%)',
                    fontWeight: 'var(--font-weight-bold)',
                  }}
                >
                  <div className={onMinimumSize ? undefined : 'line-clamp-2'} style={{ fontSize: 'var(--font-size-label)' }} title={quadrantLabels.topLeft}>
                    {quadrantLabels.topLeft}
                  </div>
                  {quadrantIconUrls?.topLeft?.length ? (
                    <div className="flex flex-wrap" style={{ marginTop: 'var(--spacing-1)', gap: 'var(--spacing-1)' }}>
                      {quadrantIconUrls.topLeft.slice(0, CHART_LIMITS.quadrantMaxIcons).map((u, idx) => (
                        <img
                          key={`qtl-${idx}`}
                          src={u}
                          alt=""
                          className="rounded object-cover border border-[color:var(--slide-border-soft)]"
                          style={{ width: 'var(--quadrant-icon-size, 1.25rem)', height: 'var(--quadrant-icon-size, 1.25rem)' }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {quadrantLabels.topRight ? (
                <div
                  ref={registerLabel('corner-topRight')}
                  className="absolute text-[color:var(--slide-text-muted)] text-right"
                  style={{
                    top: 'var(--quadrant-label-offset, 0.5rem)',
                    right: 'var(--quadrant-label-pad, 0.75rem)',
                    maxWidth: 'var(--quadrant-label-max-width, 42%)',
                    fontWeight: 'var(--font-weight-bold)',
                  }}
                >
                  <div className={onMinimumSize ? undefined : 'line-clamp-2'} style={{ fontSize: 'var(--font-size-label)' }} title={quadrantLabels.topRight}>
                    {quadrantLabels.topRight}
                  </div>
                  {quadrantIconUrls?.topRight?.length ? (
                    <div className="flex flex-wrap justify-end" style={{ marginTop: 'var(--spacing-1)', gap: 'var(--spacing-1)' }}>
                      {quadrantIconUrls.topRight.slice(0, CHART_LIMITS.quadrantMaxIcons).map((u, idx) => (
                        <img
                          key={`qtr-${idx}`}
                          src={u}
                          alt=""
                          className="rounded object-cover border border-[color:var(--slide-border-soft)]"
                          style={{ width: 'var(--quadrant-icon-size, 1.25rem)', height: 'var(--quadrant-icon-size, 1.25rem)' }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {quadrantLabels.bottomLeft ? (
                <div
                  ref={registerLabel('corner-bottomLeft')}
                  className="absolute text-[color:var(--slide-text-muted)]"
                  style={{
                    bottom: 'var(--quadrant-label-offset, 0.5rem)',
                    left: 'var(--quadrant-label-pad, 0.75rem)',
                    maxWidth: 'var(--quadrant-label-max-width, 42%)',
                    fontWeight: 'var(--font-weight-bold)',
                  }}
                >
                  <div className={onMinimumSize ? undefined : 'line-clamp-2'} style={{ fontSize: 'var(--font-size-label)' }} title={quadrantLabels.bottomLeft}>
                    {quadrantLabels.bottomLeft}
                  </div>
                  {quadrantIconUrls?.bottomLeft?.length ? (
                    <div className="flex flex-wrap" style={{ marginTop: 'var(--spacing-1)', gap: 'var(--spacing-1)' }}>
                      {quadrantIconUrls.bottomLeft.slice(0, CHART_LIMITS.quadrantMaxIcons).map((u, idx) => (
                        <img
                          key={`qbl-${idx}`}
                          src={u}
                          alt=""
                          className="rounded object-cover border border-[color:var(--slide-border-soft)]"
                          style={{ width: 'var(--quadrant-icon-size, 1.25rem)', height: 'var(--quadrant-icon-size, 1.25rem)' }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {quadrantLabels.bottomRight ? (
                <div
                  ref={registerLabel('corner-bottomRight')}
                  className="absolute text-[color:var(--slide-text-muted)] text-right"
                  style={{
                    bottom: 'var(--quadrant-label-offset, 0.5rem)',
                    right: 'var(--quadrant-label-pad, 0.75rem)',
                    maxWidth: 'var(--quadrant-label-max-width, 42%)',
                    fontWeight: 'var(--font-weight-bold)',
                  }}
                >
                  <div className={onMinimumSize ? undefined : 'line-clamp-2'} style={{ fontSize: 'var(--font-size-label)' }} title={quadrantLabels.bottomRight}>
                    {quadrantLabels.bottomRight}
                  </div>
                  {quadrantIconUrls?.bottomRight?.length ? (
                    <div className="flex flex-wrap justify-end" style={{ marginTop: 'var(--spacing-1)', gap: 'var(--spacing-1)' }}>
                      {quadrantIconUrls.bottomRight.slice(0, CHART_LIMITS.quadrantMaxIcons).map((u, idx) => (
                        <img
                          key={`qbr-${idx}`}
                          src={u}
                          alt=""
                          className="rounded object-cover border border-[color:var(--slide-border-soft)]"
                          style={{ width: 'var(--quadrant-icon-size, 1.25rem)', height: 'var(--quadrant-icon-size, 1.25rem)' }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}

          {/* Dots */}
          {limitedItems.map((it, pointIndex) => {
            // Clamp to keep dots inside chart bounds (prevents overflow on extreme values)
            const { left, bottom } = pointPosition(it)
            const quadrantKey =
              it.y >= midY
                ? it.x >= midX
                  ? ('topRight' as const)
                  : ('topLeft' as const)
                : it.x >= midX
                  ? ('bottomRight' as const)
                  : ('bottomLeft' as const)

            const dot =
              (dotColorsByName && dotColorsByName[it.name]) ||
              (dotColorMode === 'quadrant'
                ? effectiveDotColors[quadrantKey] || 'var(--slide-text-muted)'
                : colorForPoint(it.x, it.y, midX, midY).dot)

            const sizeVal = Number.isFinite(it.size as number) ? Number(it.size) : 1
            // Guard against division by zero for size normalization
            const sizeRange = sMax - sMin
            const sizeNorm = sizeRange === 0 ? 0.5 : (sizeVal - sMin) / sizeRange
            const dotSize = 10 + Math.max(0, Math.min(1, sizeNorm)) * 18

            const tooltipData = tooltipDataByName?.[it.name]
            // A stacked name steps away from the nearer edge: upward in the lower
            // half, below the dot in the upper half. Index 0 keeps today's place.
            const stackIndex = labelStackByName?.[it.name] ?? 0
            const labelStep = onMinimumSize && nativeLabelStep !== null ? `${nativeLabelStep}px` : 'var(--spacing-5)'
            const labelGap = onMinimumSize ? 'var(--ds-gap-xs)' : 'var(--spacing-0-5)'
            const labelTop =
              stackIndex === 0
                ? `calc(${labelStep} * -1)`
                : bottom < 50
                  ? `calc(${labelStep} * -${stackIndex + 1})`
                  : `calc(100% + ${labelGap} + ${labelStep} * ${stackIndex - 1})`
            return (
              <ChartTooltip key={it.name} data={tooltipData || { object_type: 'global', object_id: it.name, label: it.name }}>
                <div
                  data-testid="quadrant-chart-point"
                  className="absolute rounded-full border-white cursor-pointer group"
                  style={{
                    left: `${left}%`,
                    bottom: `${bottom}%`,
                    background: dot,
                    width: `${dotSize}px`,
                    height: `${dotSize}px`,
                    transform: 'translate(-50%, 50%)',
                    boxShadow: 'var(--shadow-sm)',
                    borderWidth: 'var(--border-width-strong)',
                    borderStyle: 'solid',
                  }}
                >
                  {showInlineLabels && (
                    <div
                      ref={registerLabel(`point-${pointIndex}`)}
                      data-testid="quadrant-chart-point-label"
                      data-stack-index={stackIndex}
                      className={cn('absolute left-1/2 -translate-x-1/2 bg-[color:var(--slide-card-bg)] text-[color:var(--slide-text-primary)] pointer-events-none', !onMinimumSize && 'truncate')}
                      style={{
                        fontSize: 'var(--font-size-label)',
                        fontWeight: 'var(--font-weight-bold)',
                        top: labelTop,
                        padding: 'var(--spacing-0-5) var(--spacing-1-5)',
                        borderRadius: 'var(--radius-sm)',
                        width: onMinimumSize ? 'max-content' : undefined,
                        maxWidth: 'clamp(9rem, 34%, 18rem)',
                      }}
                      title={it.name}
                    >
                      {it.name}
                    </div>
                  )}

              {/* Quote popup (legacy, only if feedback exists) */}
              {(it.feedback?.count ?? 0) > 0 && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 translate-y-2.5 bg-[color:var(--slide-card-bg)] border border-[color:var(--slide-border-soft)] opacity-0 invisible transition-all duration-200 ease-out group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 pointer-events-none group-hover:pointer-events-auto"
                  style={{
                    width: 'var(--quadrant-tooltip-width, 14rem)',
                    bottom: 'var(--quadrant-tooltip-offset, 1.75rem)',
                    borderRadius: 'var(--radius-ml)',
                    padding: 'var(--spacing-3)',
                    boxShadow: 'var(--shadow-md)',
                  }}
                >
                <div
                  className="border-b border-[color:var(--slide-border-soft)] flex items-center text-[color:var(--slide-success)]"
                  style={{
                    fontSize: 'var(--font-size-label)',
                    fontWeight: 'var(--font-weight-bold)',
                    marginBottom: 'var(--spacing-1-5)',
                    paddingBottom: 'var(--spacing-1)',
                    gap: 'var(--spacing-1)',
                  }}
                >
                    Feedback
                  </div>
                  <div className="space-y-2">
                    {(it.feedback?.items ?? []).slice(0, INTERACTION_LIMITS.quadrantFeedbackItems).map((q: any, idx: number) => (
                      <div key={idx} className="flex" style={{ gap: 'var(--spacing-2)' }}>
                        <div
                          className="rounded-full flex items-center justify-center text-[color:var(--slide-card-bg)]"
                          style={{
                            fontSize: 'var(--font-size-label)',
                            fontWeight: 'var(--font-weight-bold)',
                            background: 'linear-gradient(135deg, var(--slide-success) 0%, var(--slide-accent-alt) 100%)',
                            width: 'var(--spacing-6)',
                            height: 'var(--spacing-6)',
                          }}
                        >
                          {(q.initials || 'P').slice(0, 2)}
                        </div>
                        <div className="text-[color:var(--slide-text-secondary)] italic" style={{ fontSize: 'var(--font-size-label)', lineHeight: 'var(--line-height-normal)' }}>
                          “{q.text || ''}”
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
                </div>
              </ChartTooltip>
            )
          })}
        </div>
      </div>

      {/* Axis labels (in reserved gutters) */}
      <div
        className="absolute text-[color:var(--slide-text-muted)]"
        style={{
          fontSize: 'var(--font-size-caption)',
          fontWeight: 'var(--font-weight-semibold)',
          left: 0,
          top: 0,
          bottom: 'var(--quadrant-axis-offset-y, 2rem)',
          width: 'var(--quadrant-axis-offset-x, 2.25rem)',
          display: 'flex',
          flexDirection: 'column-reverse',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          pointerEvents: 'none',
        }}
      >
        {/*
          Avoid rotated+truncate: it measures width pre-rotation and ellipsizes too aggressively (e.g. "Severity" -> "S…").
          Use vertical writing-mode so the label fits the gutter without early ellipsis.
          Plots plan LB: when the gutter is too short the title ends in an ellipsis and the
          (i) above it (the end of the bottom-to-top reading) shows the whole value.
        */}
        <QuadrantYAxisTitle geometryRef={registerLabel('y-axis')} label={yAxisLabel} fullLabel={yAxisFullLabel} renderMode={renderMode} />
      </div>
      <div
        className={cn('absolute text-[color:var(--slide-text-muted)]', !onMinimumSize && 'truncate')}
        style={{
          fontSize: 'var(--font-size-caption)',
          fontWeight: 'var(--font-weight-semibold)',
          left: 'var(--quadrant-axis-offset-x, 2.25rem)',
          right: 0,
          bottom: 0,
          height: 'var(--quadrant-axis-offset-y, 2rem)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          maxWidth: 'min(22.5rem, 80%)',
          margin: '0 auto',
          gap: 4,
        }}
      >
        {/* Plots plan LB: the shared truncation rule, an ellipsis and the (i) with the whole value. */}
        <span ref={registerLabel('x-axis')} data-testid="quadrant-chart-x-axis-title" style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, pointerEvents: 'auto' }}>
          {onMinimumSize ? <span style={{ whiteSpace: 'nowrap', lineHeight: 1.5 }}>{xAxisLabel}</span> : <ChartLabel text={xAxisLabel} fullText={xAxisFullLabel} hintIndex={0} hintSize={11} maxLines={1} lineHeight={1.5} renderMode={renderMode} style={{ minWidth: 0 }} />}
        </span>
      </div>

      {showLegend && (
        <div
          className="flex flex-wrap items-center"
          style={{
            gap: 'var(--spacing-3)',
            marginTop: 'var(--spacing-2)',
            paddingLeft: 'var(--quadrant-axis-offset-x, 2.25rem)',
          }}
        >
          {limitedItems.map((it, pointIndex) => {
            const quadrantKey =
              it.y >= midY
                ? it.x >= midX ? ('topRight' as const) : ('topLeft' as const)
                : it.x >= midX ? ('bottomRight' as const) : ('bottomLeft' as const)
            const dotColor =
              (dotColorsByName && dotColorsByName[it.name]) ||
              (dotColorMode === 'quadrant'
                ? effectiveDotColors[quadrantKey] || 'var(--slide-text-muted)'
                : colorForPoint(it.x, it.y, midX, midY).dot)
            return (
              <div key={it.name} className="flex items-center" style={{ gap: 'var(--spacing-1-5)' }}>
                <span
                  className="rounded-full flex-shrink-0"
                  style={{ width: '10px', height: '10px', backgroundColor: dotColor }}
                />
                <span
                  className="text-[color:var(--slide-text-secondary)]"
                  style={{ fontSize: 'var(--font-size-label)', whiteSpace: 'nowrap' }}
                >
                  {it.name}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

